<template>
  <div class="h-full">
    <div
      class="flex flex-wrap items-center justify-between my-4 lg:my-8 sticky top-0 z-20 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-gray-200 px-1 pt-4 pb-3 relative"
    >
      <!-- title -->
      <div class="order-1 flex-grow flex items-center text-navy pr-20 md:pr-0 mb-3 md:mb-0">
        <svg-icon :path="icons.organisations" type="mdi" size="32" />

        <span class="ml-2 font-bold leading-7 text-2xl"> Organisations </span>
      </div>

      <!-- search -->
      <!-- Create Organisation button (mobile absolute) -->
      <div class="order-2 sm:order-3 sm:mr-3 absolute top-3 right-3 md:static">
        <inertia-link
          class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          :href="route('app.organisation.create')"
        >
          <svg-icon type="mdi" size="20" :path="icons.add" class="text-white" />

          <span class="sr-only"> Add Organisation </span>
        </inertia-link>
      </div>
      <!-- Search (full width on mobile) -->
      <div class="order-3 w-full mt-3 md:mt-0 md:flex-1 md:order-2 md:mr-3">
        <SearchComponent
          variant="toolbar"
          width="xl"
          :show-full-word-toggle="false"
          :form="form"
          :url="route('app.organisation.index')"
          class="w-full"
        />
      </div>
    </div>

    <flash-message />

    <!-- Mobile Card List -->
    <div v-if="organisations.length > 0" class="space-y-3 md:hidden mt-2 pb-4">
      <div v-for="(organisation, index) in organisations" :key="'org-card-'+index" class="bg-white rounded-lg border border-gray-200 p-3 shadow-sm flex flex-col gap-2">
        <div class="flex justify-between items-start gap-3">
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-semibold text-navy truncate" :title="organisation.name">{{ organisation.name }}</h3>
            <p class="text-[11px] text-gray-500 truncate" :title="organisation.type">{{ organisation.type }}</p>
          </div>
          <div class="shrink-0">
            <index-menu-item :links="indexLinks(organisation)" />
          </div>
        </div>
        <div class="grid grid-cols-3 gap-3 text-[11px] leading-tight text-gray-700">
          <div>
            <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Farmers</p>
            <div class="font-semibold">{{ organisation.farmers }}</div>
          </div>
          <div>
            <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Tasks</p>
            <div class="font-semibold">{{ organisation.tasks }}</div>
          </div>
          <div>
            <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Contact</p>
            <div class="truncate" :title="organisation.telephone + ' ' + organisation.email">{{ organisation.telephone }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table View (Desktop) -->
    <div v-if="organisations.length > 0" class="hidden md:block">
      <div class="mt-2 pb-14 w-full overflow-x-auto">
        <table
          class="table-auto min-w-[760px] xs:!mr-0 w-full divide-y divide-gray-400 border-b border-gray-400 border-collapse rounded-lg"
        >
          <thead>
            <tr class="bg-gray-200 font-bold text-sm text-gray-900">
              <td class="px-2 py-2.5 whitespace-nowrap">Name</td>
              <td class="px-2 py-2.5 whitespace-nowrap">Contact</td>
              <td class="px-2 py-2.5 whitespace-nowrap">Farmers</td>
              <td class="px-2 py-2.5 whitespace-nowrap">Tasks</td>
              <td class="px-2 py-2.5 w-10"></td>
            </tr>
          </thead>

          <tbody class="bg-white">
            <tr
              class="odd:bg-white even:bg-slate-100 text-gray-900 text-sm"
              v-for="(organisation, index) in organisations"
              :key="index"
            >
              <td class="px-2 py-2.5 whitespace-nowrap truncate" :title="organisation.name + ' (' + organisation.type + ')'">
                {{ organisation.name }}
                <span class="text-gray-500"> ({{ organisation.type }})</span>
              </td>
              <td class="px-2 py-2.5 whitespace-nowrap truncate" :title="organisation.telephone + ' ' + organisation.email">
                {{ organisation.telephone }}
                <br />
                {{ organisation.email }}
              </td>
              <td class="px-2 py-2.5 text-center">
                <div
                  class="w-7 h-7 font-bold text-[11px] bg-gray-600 flex items-center justify-center text-white rounded-full mx-auto"
                >
                  <inertia-link
                    :href="route('app.farmer.index') + '?organisation=' + organisation.id"
                  >
                    {{ organisation.farmers }}
                  </inertia-link>
                </div>
              </td>
              <td class="px-2 py-2.5 text-center">
                <div
                  class="w-7 h-7 font-bold text-[11px] bg-gray-600 flex items-center justify-center text-white rounded-full mx-auto"
                >
                  {{ organisation.tasks }}
                </div>
              </td>
              <td class="px-2 py-2.5 w-10 text-right">
                <index-menu-item :links="indexLinks(organisation)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-center">
        <pagination class="mt-8 mb-4" :links="links" />
      </div>
    </div>

    <div v-if="organisations.length === 0" class="bg-white mt-8 px-6 py-4">
      <strong>No organisations found.</strong>
    </div>
  </div>
</template>

<script>
import AppLayout from "@/Layouts/App";
import Pagination from "@/Components/Pagination";
import IndexMenuItem from "@/Components/IndexMenuItem";
import SearchComponent from "@/Components/SearchComponent";
import _ from "lodash";
import { add, organisations } from "@/Partials/icons";

export default {
  name: "organisationManager",
  layout: [AppLayout],
  components: {
    Pagination,
    IndexMenuItem,
    SearchComponent,
  },
  props: {
    search: String,
    results: Object,
    paginate: String,
  },

  data() {
    return {
      form: {
        search: this.search,
      },
      organisations: this.results["original"]["data"],
      links: this.results["original"]["meta"]["pagination"],
      link: this.paginate,
      icons: {
        add,
        organisations,
      },
    };
  },

  methods: {
    indexLinks(organisation) {
      return {
        View: route("app.organisation.view", organisation.id),
        Edit: route("app.organisation.edit", organisation.id),
      };
    },
  },
};
</script>
